import React from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@xingternal/button';

import { useCompanyData } from '@/contexts/CompaniesDataContext';
import { useFollowCompanyMutation } from '@/hooks/useFollowCompanyMutation';
import { useUnfollowCompanyMutation } from '@/hooks/useUnfollowCompanyMutation';
import { trackFollowCompany, trackUnfollowCompany } from '@/utils/tracking';

import { Maybe } from '@/types/graphql-types';

const FollowButton = ({
  isFollowing,
  entityPageId,
  companyId,
  disabled,
  followText = 'CP_200_FOLLOW_LINK',
  className,
}: {
  isFollowing: boolean;
  entityPageId: string;
  companyId: Maybe<string>;
  followText?: string;
  className: boolean;
  disabled: boolean;
}) => {
  const { handleFollow } = useFollowCompanyMutation({
    entityPageId,
  });
  const { handleUnfollow } = useUnfollowCompanyMutation({
    entityPageId,
  });
  const { globalCompaniesState } = useCompanyData();

  const resolveIsFollowing = () => {
    const company = globalCompaniesState.find(
      (item) => item?.entityPageId === entityPageId,
    );

    if (!company?.userContext?.followState) return isFollowing;

    return company.userContext.followState.isFollowing;
  };

  const follow = ({ companyId }: { companyId: Maybe<string> }) => {
    trackFollowCompany(companyId);
    handleFollow();
  };

  const unfollow = ({ companyId }: { companyId: Maybe<string> }) => {
    trackUnfollowCompany(companyId);
    handleUnfollow();
  };

  const resolvedIsFollowing = resolveIsFollowing();

  return (
    <Button
      size="small"
      variant="secondary"
      disabled={disabled}
      className={className}
      onClick={
        resolvedIsFollowing
          ? () => unfollow({ companyId })
          : () => follow({ companyId })
      }
    >
      <FormattedMessage
        id={resolvedIsFollowing ? 'CP_200_UNFOLLOW_LINK' : followText}
      />
    </Button>
  );
};

export { FollowButton };
